import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "../components/Layout";
import KundaliChart from "../components/KundaliChart";
import api from "../lib/api";
import { toast } from "sonner";
import {
  PaperPlaneTilt, SignOut, Star, IdentificationCard, MapPin, Clock,
  CalendarBlank, ChatCircleDots, Phone, PhoneSlash, Wallet, ChartLineUp,
  Users, ChatTeardropDots, ArrowsClockwise, Sparkle, X, ArrowDown,
  Bank, ShieldCheck, PencilSimple, Warning, Microphone, MicrophoneSlash,
  ListBullets, MoonStars, Bell, BellSlash,
} from "@phosphor-icons/react";
import { useTwilioVoice } from "../components/VoiceCall";
import usePushNotifications from "../hooks/usePushNotifications";

/**
 * Human-astrologer portal dashboard.
 *
 * UX goals (mandated by product):
 *  • Two BIG independent toggles — Chat ON/OFF and Call ON/OFF. Flipping
 *    Chat ON instantly bubbles the astrologer to the very top of the
 *    public /astrologers directory (handled in backend list ordering).
 *  • Wallet card: total unwithdrawn earnings + today / 7-day / 30-day
 *    revenue. Lifetime gross for transparency.
 *  • Past conversations list (seeker name + date + status).
 *  • Past reviews from seekers — including the lower-star ones that are
 *    HIDDEN on the public profile, so the astrologer sees honest feedback.
 *  • Inline chat panel kept for replying in-portal.
 */

const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AstrologerDashboardPage() {
  const navigate = useNavigate();
  // Query params used by the GlobalAstrologerRing banner so we can
  // auto-open a chat / voice-call in full-screen the moment we land here.
  const [searchParams, setSearchParams] = useSearchParams();
  const [astro, setAstro] = useState(null);
  const [stats, setStats] = useState(null);
  const [chats, setChats] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [context, setContext] = useState(null);
  const [showDashas, setShowDashas] = useState(false);
  const [showFullChart, setShowFullChart] = useState(false);
  const [priorChatsMeta, setPriorChatsMeta] = useState([]);
  // Drives the "Seeker ended this chat" banner. Set when our status poll
  // sees the chat flip from active → ended without the astrologer
  // explicitly ending it. Reset whenever a new chat is opened.
  const [endedBySeeker, setEndedBySeeker] = useState(false);
  const [text, setText] = useState("");
  const [toggling, setToggling] = useState({ chat: false, call: false });
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showPayoutEditor, setShowPayoutEditor] = useState(false);
  // When the astrologer ACCEPTS an incoming chat / call, we promote the
  // active-chat panel into a fixed full-screen overlay so they don't have
  // to hunt for it on the dashboard. They can collapse it back to the
  // inline grid at any time via the "Minimize" header button.
  const [chatFullscreen, setChatFullscreen] = useState(false);
  const lastTs = useRef(null);

  // Voice — register as Twilio Device when astrologer flips Call ON, so
  // their browser is ringable. We auto-register lazily; the SDK won't
  // grab the mic until a real incoming call.
  const callOnState = !!astro?.call_enabled;
  const voice = useTwilioVoice({
    role: "astrologer",
    userId: astro?.astrologer_id || "",
    autoRegister: false,
  });
  useEffect(() => {
    if (callOnState && astro?.astrologer_id) {
      voice.ensureDevice().catch((e) => {
        console.warn("[Astrologer voice register failed]", e);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callOnState, astro?.astrologer_id]);

  // Incoming chat polling — when chat_enabled is ON, poll every 3s for
  // ringing chats and play a ringtone until the astrologer accepts or
  // declines. Mirrors the Twilio incoming-call UX.
  const [incoming, setIncoming] = useState(null);
  const [incomingContext, setIncomingContext] = useState(null);
  const beepRef = useRef(null);
  const chatOnState = !!astro?.chat_enabled;
  useEffect(() => {
    if (!chatOnState) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/astrologer/chats/incoming");
        if (data && data.length > 0) {
          // Always pick the oldest ringing chat (FIFO).
          const next = data[data.length - 1];
          setIncoming((cur) => {
            // Don't replace the current ringing modal — the astrologer
            // is already deciding on one; the next one will surface
            // after they accept/decline.
            if (cur && cur.chat_id === next.chat_id) return cur;
            if (cur) return cur;
            return next;
          });
        }
      } catch { /* swallow polling errors */ }
      if (!cancelled) setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [chatOnState]);

  // Beep loop — Web Audio API generates a soft 880Hz blip every 1.5s
  // while a chat is incoming. Stops cleanly on accept/decline/unmount.
  useEffect(() => {
    if (!incoming) {
      if (beepRef.current) { beepRef.current.stop(); beepRef.current = null; }
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    let cancelled = false;
    const ctx = new Ctx();
    const blip = () => {
      if (cancelled) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      g.gain.value = 0.0001;
      o.connect(g).connect(ctx.destination);
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      o.start(now);
      o.stop(now + 0.4);
      setTimeout(blip, 1500);
    };
    beepRef.current = {
      stop: () => {
        cancelled = true;
        // ctx.close() returns a Promise — chain catch to swallow the
        // "AudioContext is already closed" rejection on double-close.
        try { ctx.close().catch(() => {}); } catch { /* sync-throw: ignore */ }
      },
    };
    blip();
    return () => {
      cancelled = true;
      try { ctx.close().catch(() => {}); } catch { /* sync-throw: ignore */ }
    };
  }, [incoming]);

  // Pre-fetch the seeker's chart context the moment a ringing chat
  // appears — so the astrologer sees birth-details + lagna + dasha tree
  // BEFORE they even click Accept. <1s prep for fast predictions.
  useEffect(() => {
    if (!incoming?.chat_id) { setIncomingContext(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/astrologer/chats/${incoming.chat_id}/context`);
        if (!cancelled) setIncomingContext(data);
      } catch { /* ignore — modal still works without context */ }
    })();
    return () => { cancelled = true; };
  }, [incoming?.chat_id]);

  // ─── Incoming CALL polling (PSTN + WebRTC). Mirrors the chat poll
  // above but for voice calls. Gated by call_enabled so we don't ring
  // an astrologer who's not taking calls. The Twilio leg actually
  // rings their phone — this is just a heads-up + chart-context popup
  // so they walk into the call with the seeker's birth chart prepped.
  const [incomingCall, setIncomingCall] = useState(null);
  const [dismissedCallIds, setDismissedCallIds] = useState(() => new Set());
  useEffect(() => {
    if (!callOnState) { setIncomingCall(null); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/astrologer/calls/incoming");
        if (Array.isArray(data) && data.length > 0) {
          // Newest ringing call that the astrologer hasn't dismissed.
          const next = data.find((c) => !dismissedCallIds.has(c.call_id)) || null;
          setIncomingCall(next);
        } else {
          setIncomingCall(null);
        }
      } catch { /* swallow polling errors */ }
      if (!cancelled) setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [callOnState, dismissedCallIds]);

  // ─── Auto-open the call's row in Past Conversations the moment a new
  // call appears (PSTN or WebRTC). The astrologer gets the chart +
  // dasha context auto-loaded on the right panel without having to
  // click the row. Triggers once per unique call_id so we don't
  // hijack focus if the astrologer manually navigated elsewhere.
  const autoOpenedCallIdRef = useRef(null);
  useEffect(() => {
    const cid = incomingCall?.call_id;
    if (!cid) return;
    if (autoOpenedCallIdRef.current === cid) return;
    autoOpenedCallIdRef.current = cid;
    const chatRow = {
      chat_id: `vc_${cid}`,
      customer_name: incomingCall.seeker_name || "Phone seeker",
      customer_id: incomingCall.seeker_id,
      status: "active",
      source: "voice",
      voice_mode: incomingCall.mode,
      started_at: incomingCall.created_at,
    };
    // openChat() is async but we don't need to await — UI state is set
    // synchronously inside it, the async parts (messages + context) settle
    // in the background and just update the panel when they arrive.
    openChat(chatRow).catch(() => { /* swallow — partial load is OK */ });
    // Voice call — also drop the astrologer into the focused full-screen
    // chat view so the seeker's chart + chat thread are right there while
    // they're on the call.
    setChatFullscreen(true);
  }, [incomingCall?.call_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Deep-link entry from the GlobalAstrologerRing banner ─────────────
  // The banner navigates here with ?fullscreen_chat={chat_id} (or
  // ?fullscreen_call={call_id}) after the astrologer hits Accept from
  // any other page. We wait for `chats` to load, then auto-open the
  // matching row in the full-screen takeover so the astrologer drops
  // straight into the conversation without an extra click. Param is
  // consumed (cleared from the URL) so a refresh doesn't re-trigger.
  const deepLinkConsumedRef = useRef(null);
  useEffect(() => {
    const wantChat = searchParams.get("fullscreen_chat");
    const wantCall = searchParams.get("fullscreen_call");
    const wanted = wantChat || wantCall;
    if (!wanted) return;
    if (deepLinkConsumedRef.current === wanted) return;
    // chat list still loading — wait until refresh() populates it.
    if (!chats || chats.length === 0) return;
    if (wantChat) {
      const row = chats.find((c) => c.chat_id === wantChat);
      if (!row) return; // not in the list yet — let the next poll catch it
      deepLinkConsumedRef.current = wantChat;
      openChat(row).catch(() => { /* partial load OK */ });
      setChatFullscreen(true);
    } else if (wantCall) {
      // Voice calls don't live in `chats` — they're synthesized from
      // `incomingCall`. Just open a thin chat row keyed off the call_id;
      // the voice-call auto-open effect above will hydrate the rest.
      deepLinkConsumedRef.current = wantCall;
      const synthetic = {
        chat_id: `vc_${wantCall}`,
        customer_name: "Phone seeker",
        status: "active",
        source: "voice",
      };
      openChat(synthetic).catch(() => { /* partial load OK */ });
      setChatFullscreen(true);
    }
    // Strip the query param so a soft-refresh doesn't re-trigger.
    setSearchParams({}, { replace: true });
  }, [chats, searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Audio cue for incoming voice calls, differentiated by mode:
  //  • PSTN → THREE short blips, ONE-SHOT. The astrologer's actual phone
  //    is already ringing via Twilio, so the dashboard only needs a heads-up.
  //  • WebRTC → continuous ringtone loop until accepted or dismissed,
  //    since there is no separate phone ring — the browser IS the ring.
  // Tracked per call_id so the alert plays exactly once per new call.
  const callBeepRef = useRef(null);
  const lastAlertedCallIdRef = useRef(null);
  useEffect(() => {
    // Always tear down the previous loop when the active call changes or clears.
    if (callBeepRef.current) {
      try { callBeepRef.current.stop(); } catch { /* noop */ }
      callBeepRef.current = null;
    }
    const cid = incomingCall?.call_id;
    if (!cid) return;
    // Don't re-alert for the same call across re-renders / poll cycles.
    if (lastAlertedCallIdRef.current === cid) return;
    lastAlertedCallIdRef.current = cid;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const blip = (when, freq = 880, duration = 0.18, gain = 0.18) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.value = 0.0001;
      o.connect(g).connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, when);
      g.gain.exponentialRampToValueAtTime(gain, when + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, when + duration);
      o.start(when);
      o.stop(when + duration + 0.02);
    };

    if (incomingCall.mode === "pstn") {
      // Three quick beeps at 880Hz, 200ms apart — done.
      const t0 = ctx.currentTime + 0.02;
      blip(t0);
      blip(t0 + 0.35);
      blip(t0 + 0.70);
      const stopAt = (t0 + 0.95) * 1000 - ctx.currentTime * 1000;
      const timer = setTimeout(() => { try { ctx.close().catch(() => {}); } catch { /* noop */ } }, Math.max(stopAt, 1200));
      callBeepRef.current = { stop: () => { clearTimeout(timer); try { ctx.close().catch(() => {}); } catch { /* noop */ } } };
    } else {
      // WebRTC — classic two-tone ringtone loop (ring-ring … pause).
      let cancelled = false;
      const tick = () => {
        if (cancelled) return;
        const now = ctx.currentTime;
        blip(now,           750, 0.40, 0.20);
        blip(now + 0.50,    600, 0.40, 0.20);
        setTimeout(tick, 2500);
      };
      callBeepRef.current = {
        stop: () => { cancelled = true; try { ctx.close().catch(() => {}); } catch { /* noop */ } },
      };
      tick();
    }

    return () => {
      if (callBeepRef.current) {
        try { callBeepRef.current.stop(); } catch { /* noop */ }
        callBeepRef.current = null;
      }
    };
  }, [incomingCall?.call_id, incomingCall?.mode]);

  const acceptIncomingChat = async () => {
    if (!incoming) return;
    try {
      await api.post(`/astrologer/chats/${incoming.chat_id}/accept`);
      toast.success(`Connected with ${incoming.customer_name}`);
      // Pull fresh chats and auto-open the just-accepted one
      await refresh();
      const fresh = await api.get(`/astrologer/chats`);
      const justAccepted = (fresh.data || []).find((c) => c.chat_id === incoming.chat_id);
      if (justAccepted) {
        await openChat(justAccepted);
        // Promote to fullscreen overlay so astrologer drops straight into a
        // focused chat window (same UX as the seeker's ChatPage view).
        setChatFullscreen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept chat.");
    }
    setIncoming(null);
  };
  const declineIncomingChat = async () => {
    if (!incoming) return;
    try {
      await api.post(`/astrologer/chats/${incoming.chat_id}/decline`);
      toast.message("Chat declined");
    } catch { /* ignore */ }
    setIncoming(null);
  };
  const pollRef = useRef(null);
  const scrollRef = useRef(null);

  // Bootstrap — auth + initial data fetch happen together.
  //
  // Resilience fix: previously this used a single `Promise.all().catch()`
  // which meant ANY one of the 4 calls failing (e.g. a transient 502 on
  // dashboard-stats during cluster cold-start) redirected the user to
  // /astrologer/login — which then bounces them right back here because
  // their astro_token cookie is still valid. The visible symptom was
  // "Loading…" stuck or rapid sign-out → sign-in flicker.
  //
  // Now: /astrologer/me is the ONLY call whose failure means real auth
  // loss. The other 3 are best-effort — they retry once and otherwise
  // settle to empty state without nuking the session.
  useEffect(() => {
    let cancelled = false;
    const fetchWithRetry = async (url, retries = 1) => {
      for (let i = 0; i <= retries; i++) {
        try {
          const { data } = await api.get(url);
          return data;
        } catch (e) {
          if (i === retries) throw e;
          await new Promise((r) => setTimeout(r, 600 * (i + 1)));
        }
      }
    };
    (async () => {
      // 1. Auth check — this MUST succeed; if it fails it's a real session loss.
      let me;
      try {
        me = await fetchWithRetry("/astrologer/me", 1);
      } catch {
        // Genuine auth failure — only NOW redirect to login.
        navigate("/astrologer/login");
        return;
      }
      if (cancelled) return;
      setAstro(me);
      // 2. Non-critical data fetches — failures here must NOT log the
      //    astrologer out. Settle to empty / cached state on error.
      const [c, s, r] = await Promise.allSettled([
        fetchWithRetry("/astrologer/chats", 1),
        fetchWithRetry("/astrologer/dashboard-stats", 1),
        fetchWithRetry("/astrologer/reviews", 1),
      ]);
      if (cancelled) return;
      if (c.status === "fulfilled") setChats(c.value || []);
      if (s.status === "fulfilled") setStats(s.value || null);
      if (r.status === "fulfilled") setReviews(r.value || []);
      // If anything fell off, the dashboard is still usable — toast the
      // user a non-blocking nudge so they know to retry manually.
      if ([c, s, r].some((p) => p.status === "rejected")) {
        toast.message("Some panels couldn't load. Tap refresh to retry.", { duration: 4000 });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const refresh = async () => {
    try {
      const [a, c, s, r] = await Promise.all([
        api.get("/astrologer/me"),
        api.get("/astrologer/chats"),
        api.get("/astrologer/dashboard-stats"),
        api.get("/astrologer/reviews"),
      ]);
      setAstro(a.data);
      setChats(c.data || []);
      setStats(s.data || null);
      setReviews(r.data || []);
    } catch {
      toast.error("Couldn't refresh dashboard");
    }
  };

  const setAvailability = async (key, val) => {
    setToggling((p) => ({ ...p, [key === "chat_enabled" ? "chat" : "call"]: true }));
    try {
      const { data } = await api.patch("/astrologer/me", { [key]: val });
      setAstro(data);
      if (key === "chat_enabled") {
        toast.success(val ? "🔥 You're LIVE for chats — listed at top now." : "Chat turned off.");
      } else {
        toast.success(val ? "📞 You're LIVE for calls." : "Calls turned off.");
      }
    } catch {
      toast.error("Couldn't update availability. Try again.");
    } finally {
      setToggling((p) => ({ ...p, [key === "chat_enabled" ? "chat" : "call"]: false }));
    }
  };

  const openChat = async (c) => {
    setActiveChat(c);
    setMessages([]);
    setContext(null);
    setEndedBySeeker(false);
    setShowDashas(false);
    setShowFullChart(false);
    setPriorChatsMeta([]);
    lastTs.current = null;
    // Pull current chat's messages + context + prior conversations
    // with this same seeker (so the astrologer has the relationship
    // history loaded right above today's first message).
    const [{ data: msgs }, ctxRes, priorRes] = await Promise.all([
      api.get(`/chats/${c.chat_id}/messages`),
      api.get(`/astrologer/chats/${c.chat_id}/context`).catch(() => ({ data: null })),
      api.get(`/astrologer/chats/${c.chat_id}/prior-messages`).catch(() => ({ data: null })),
    ]);
    // Stitch: prior messages → "── Previous conversations ──" divider
    // → today's messages. The divider is a synthetic system message the
    // chat panel renders distinctively (sender_role === "system").
    let combined = [];
    const prior = priorRes?.data?.messages || [];
    if (prior.length > 0) {
      combined = [
        ...prior,
        {
          message_id: "__prior_divider__",
          chat_id: c.chat_id,
          sender_role: "system",
          text: `── Previous conversations with ${c.customer_name} (${prior.length} messages across ${priorRes?.data?.chats?.length || 0} session${(priorRes?.data?.chats?.length || 0) === 1 ? "" : "s"}) ──`,
          created_at: msgs[0]?.created_at || new Date().toISOString(),
          is_divider: true,
        },
      ];
      setPriorChatsMeta(priorRes?.data?.chats || []);
    }
    combined = [...combined, ...msgs];
    setMessages(combined);
    if (ctxRes?.data) setContext(ctxRes.data);
    if (msgs.length) lastTs.current = msgs[msgs.length - 1].created_at;
  };

  // Status poll — every 5s while a chat is active, ping
  // /astrologer/chats/{id} to detect when the seeker ends the chat.
  // Flips the local copy + raises a banner inside the active-chat panel.
  useEffect(() => {
    if (!activeChat || activeChat.status !== "active") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/astrologer/chats/${activeChat.chat_id}`);
        if (cancelled) return;
        if (data?.status === "ended" && activeChat.status === "active") {
          setActiveChat((prev) => prev ? { ...prev, status: "ended", ended_at: data.ended_at } : prev);
          setEndedBySeeker(true);
          // Audible cue + toast so the astrologer notices even if they
          // were typing or scrolled away.
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              const o = ctx.createOscillator(); const g = ctx.createGain();
              o.connect(g); g.connect(ctx.destination);
              o.type = "sine"; o.frequency.value = 440;
              g.gain.setValueAtTime(0.001, ctx.currentTime);
              g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.05);
              g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
              o.start(); o.stop(ctx.currentTime + 0.65);
            }
          } catch { /* audio is best-effort */ }
          toast.message(`${activeChat.customer_name} ended the chat`, {
            description: data.ended_at
              ? new Date(data.ended_at).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit" })
              : undefined,
          });
        }
      } catch { /* ignore polling errors */ }
    };
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeChat]);

  useEffect(() => {
    if (!activeChat) return;
    pollRef.current = setInterval(async () => {
      const { data } = await api.get(`/chats/${activeChat.chat_id}/messages`, {
        params: lastTs.current ? { after: lastTs.current } : {},
      });
      if (data.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          return [...prev, ...data.filter((m) => !seen.has(m.message_id))];
        });
        lastTs.current = data[data.length - 1].created_at;
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [activeChat]);

  // Live wallet refresh — while the astrologer has an active chat
  // open, the seeker side runs a 15s heartbeat that ticks total_charged
  // upward. We re-pull dashboard-stats every 30s so the wallet & today
  // revenue numbers visibly update without a page reload.
  useEffect(() => {
    if (!activeChat || activeChat.status !== "active") return;
    const id = setInterval(async () => {
      try {
        const { data: s } = await api.get("/astrologer/dashboard-stats");
        setStats(s);
      } catch { /* ignore polling errors */ }
    }, 30000);
    return () => clearInterval(id);
  }, [activeChat]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Typing indicator — astrologer-side. Polls the same /typing-status
  // endpoint the seeker uses; shows "{seeker} is typing…" dots when
  // the seeker has typed in the last 5s.
  const [seekerTyping, setSeekerTyping] = useState(false);
  const lastTypingPingRef = useRef(0);
  useEffect(() => {
    if (!activeChat || activeChat.status === "ended") { setSeekerTyping(false); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/chats/${activeChat.chat_id}/typing-status`);
        if (!cancelled) setSeekerTyping(!!data?.customer_typing);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeChat?.chat_id, activeChat?.status]);
  // Throttled "astrologer is typing" heartbeat (max once per 2.5s).
  const pingAstroTyping = () => {
    if (!activeChat?.chat_id) return;
    const now = Date.now();
    if (now - lastTypingPingRef.current < 2500) return;
    lastTypingPingRef.current = now;
    api.post(`/astrologer/chats/${activeChat.chat_id}/typing`).catch(() => { /* noop */ });
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeChat) return;
    // Astrologer can still send POST-end messages (follow-up notes /
    // recharge nudges) regardless of who ended the chat. The backend
    // tags these with `post_chat: true`; no billing happens because
    // the chat status is already `ended`.
    const t = text.trim();
    setText("");
    try {
      const { data: msg } = await api.post(`/astrologer/chats/${activeChat.chat_id}/messages`, { text: t });
      setMessages((p) => [...p, msg]);
      lastTs.current = msg.created_at;
    } catch (err) {
      // If the seeker just ended the chat in the gap between our last
      // poll and the send, surface that gracefully.
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 400 && /ended/i.test(detail)) {
        setEndedBySeeker(true);
        setActiveChat((prev) => prev ? { ...prev, status: "ended" } : prev);
        toast.error(`${activeChat.customer_name} just ended the chat.`);
      } else {
        toast.error("Couldn't send. Try again.");
        setText(t);
      }
    }
  };

  // Astrologer-side end-chat. Confirmed via window.confirm so a stray
  // tap doesn't terminate a paid session. Updates local state instantly
  // and lets the queue-advance fire in the background.
  const [endingChat, setEndingChat] = useState(false);
  const endChatFromAstro = async () => {
    if (!activeChat?.chat_id || endingChat) return;
    if (activeChat.status === "ended") return;
    if (!window.confirm(`End your chat with ${activeChat.customer_name}? They'll be billed for any unbilled seconds.`)) return;
    setEndingChat(true);
    try {
      const { data } = await api.post(`/astrologer/chats/${activeChat.chat_id}/end`);
      setActiveChat((prev) => prev ? { ...prev, status: "ended" } : prev);
      // Also reflect in the past-conversations list so the row flips to ENDED.
      setChats((prev) => prev.map((c) => c.chat_id === activeChat.chat_id ? { ...c, status: "ended" } : c));
      const charged = data?.final_charge || 0;
      const mins = data?.final_minutes_charged || 0;
      toast.success(
        charged > 0
          ? `Chat ended. Settled ${mins} min · ₹${charged.toFixed(2)}.`
          : "Chat ended.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't end chat. Try again.");
    } finally {
      setEndingChat(false);
    }
  };

  const logout = async () => {
    try { await api.post("/astrologer/logout"); } catch { /* still navigate to login */ }
    navigate("/astrologer/login");
  };

  // Distinct seekers + last-seen map → drives the "Past conversations" list
  // even when there are multiple chat docs with the same seeker.
  const seekerHistory = useMemo(() => {
    const map = new Map();
    for (const c of chats) {
      const k = c.customer_id || c.customer_name || c.chat_id;
      const prev = map.get(k);
      const ts = c.started_at;
      if (!prev || (ts && ts > prev.started_at)) {
        map.set(k, c);
      }
    }
    return Array.from(map.values()).sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""));
  }, [chats]);

  if (!astro || !stats) {
    return (
      <PageShell>
        <div className="max-w-7xl mx-auto px-6 py-24 text-muted" data-testid="astro-dashboard-loading">
          Loading…
        </div>
      </PageShell>
    );
  }

  const chatOn = !!astro.chat_enabled;
  const callOn = !!astro.call_enabled;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-24" data-testid="astrologer-dashboard">

        {/* ─── Header (identity + global actions) ─── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src={astro.avatar}
              alt={astro.name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-amber-300 shadow"
              data-testid="astro-avatar"
            />
            <div>
              <p className="label-eyebrow">Astrologer portal</p>
              <h1 className="font-serif-display text-3xl sm:text-5xl mt-1 font-light" data-testid="astro-greeting">
                Hi, {astro.name.split(" ")[0]}
              </h1>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-muted mt-2">
                <span className="flex items-center gap-1">
                  <Star size={14} weight="fill" className="text-gold" />
                  {stats.average_rating?.toFixed(1) || astro.rating}
                </span>
                <span>·</span>
                <span>{stats.reviews_count} reviews</span>
                <span>·</span>
                <span className="font-medium text-[#3F2D1B]">{INR(astro.rate_per_min)}/min</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} className="btn-ghost rounded-full px-3 py-2 text-xs flex items-center gap-1.5" data-testid="dashboard-refresh-btn">
              <ArrowsClockwise size={14} /> Refresh
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("Force-end every active chat and call, and clear queued waiters?\n\nUse this only when the dashboard is stuck and users see 'astrologer is busy' but you can't see the session.")) return;
                try {
                  const { data } = await api.post("/astrologer/sessions/end-all");
                  toast.success(`Ended ${data.chats_ended} chat(s), ${data.calls_ended} call(s), cleared ${data.queue_cleared} waiter(s).`);
                  refresh();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't end sessions");
                }
              }}
              className="rounded-full px-3 py-2 text-xs flex items-center gap-1.5 bg-rose-600 hover:bg-rose-500 text-white font-semibold shadow"
              data-testid="astro-end-all-sessions-btn"
              title="Force-end every active chat and call and clear the queue (use when dashboard is stuck)"
            >
              <SignOut size={14} weight="bold" /> End All
            </button>
            <button onClick={logout} className="btn-ghost rounded-full p-2.5" data-testid="astro-logout" title="Sign out">
              <SignOut size={16} />
            </button>
          </div>
        </div>

        {/* ─── Availability toggles ─── */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="availability-row">
          <AvailabilityCard
            label="Chat"
            description={chatOn ? "Listed at the top of /astrologers. Seekers can ping you any minute." : "Flip ON to be listed first & start earning."}
            icon={ChatCircleDots}
            on={chatOn}
            busy={toggling.chat}
            onChange={(v) => setAvailability("chat_enabled", v)}
            testid="toggle-chat"
          />
          <AvailabilityCard
            label="Call"
            description={callOn ? "Voice/video calls are live for incoming seekers." : "Flip ON when you're ready to take live calls."}
            icon={Phone}
            on={callOn}
            busy={toggling.call}
            onChange={(v) => setAvailability("call_enabled", v)}
            testid="toggle-call"
          />
        </div>

        {/* ─── Phone-style push notifications for incoming chats/calls ─── */}
        <PushNotifBanner />

        {/* ─── Calling phone number (PSTN bridge target) ─── */}
        {callOn && (
          <PhoneNumberRow
            existing={stats.phone}
            onSaved={refresh}
          />
        )}

        {/* ─── Wallet + Revenue ─── */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="earnings-row">
          {/* Wallet (lifetime - paid_out - pending) */}
          <div className="lg:col-span-1 rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#2A1F12] to-[#1A1410] text-amber-50 p-6 shadow-xl border border-amber-700/30 flex flex-col" data-testid="wallet-card">
            <div className="flex items-center justify-between">
              <p className="label-eyebrow !text-amber-300">Wallet · available</p>
              <Wallet size={22} weight="duotone" className="text-amber-300" />
            </div>
            <div className="font-serif-display text-4xl sm:text-5xl mt-3" data-testid="wallet-amount">{INR(stats.wallet_balance)}</div>
            <div className="text-xs text-amber-200/70 mt-2 space-y-0.5">
              <div>Lifetime gross: <span className="text-amber-100">{INR(stats.lifetime_earnings)}</span></div>
              {stats.paid_out > 0 && <div>Paid out: <span className="text-amber-100">{INR(stats.paid_out)}</span></div>}
              {stats.pending_withdrawals > 0 && (
                <div data-testid="wallet-pending">
                  Pending withdrawal: <span className="text-amber-100">{INR(stats.pending_withdrawals)}</span>
                </div>
              )}
            </div>
            <div className="mt-4 text-xs text-amber-200/80">
              Rate: <span className="font-medium text-amber-100">{INR(astro.rate_per_min)}/min</span>
            </div>

            {/* Payout destination strip — sits right above the Withdraw CTA so
                the astrologer always knows where the money is going BEFORE
                they tap the button. Missing → red warning + clickable hint. */}
            <PayoutPill
              account={stats.payout_account}
              onEdit={() => setShowPayoutEditor(true)}
            />

            <button
              type="button"
              onClick={() => {
                if (!stats.payout_account) {
                  setShowPayoutEditor(true);
                  toast.message("Add a payout method first", {
                    description: "We can't promise a 7-day delivery without UPI or bank rails.",
                  });
                  return;
                }
                setShowWithdraw(true);
              }}
              disabled={stats.wallet_balance <= 0}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 text-[#1A1410] shadow-md ring-1 ring-amber-200/60 hover:brightness-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="withdraw-btn"
            >
              <ArrowDown size={16} weight="bold" /> Withdraw balance
            </button>
          </div>

          {/* Revenue windows */}
          <div className="lg:col-span-2 grid grid-cols-3 gap-3" data-testid="revenue-windows">
            <RevenueStat label="Today" value={stats.revenue_today} testid="rev-today" />
            <RevenueStat label="Last 7 days" value={stats.revenue_week} testid="rev-week" />
            <RevenueStat label="Last 30 days" value={stats.revenue_month} testid="rev-month" />
            <div className="col-span-3 card-surface rounded-2xl px-5 py-4 flex items-center justify-between" data-testid="lifetime-row">
              <div>
                <p className="label-eyebrow flex items-center gap-1.5"><Users size={12} weight="fill" /> Seekers served</p>
                <p className="font-serif-display text-2xl mt-1">{stats.unique_seekers}</p>
              </div>
              <div className="text-right">
                <p className="label-eyebrow flex items-center justify-end gap-1.5"><ChatTeardropDots size={12} weight="fill" /> Chats total</p>
                <p className="font-serif-display text-2xl mt-1">{stats.chats_total}</p>
              </div>
              <div className="text-right">
                <p className="label-eyebrow flex items-center justify-end gap-1.5"><ChartLineUp size={12} weight="fill" /> Lifetime</p>
                <p className="font-serif-display text-2xl mt-1">{INR(stats.lifetime_earnings)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Conversations + Active chat ─── */}
        <div className="mt-10 grid lg:grid-cols-12 gap-4">
          {/* Left: past conversations */}
          <div className="lg:col-span-4 card-surface rounded-2xl p-5 max-h-[70vh] overflow-y-auto" data-testid="conversations-card">
            <div className="flex items-center justify-between mb-4">
              <p className="label-eyebrow">Past conversations ({seekerHistory.length})</p>
            </div>
            {seekerHistory.length === 0 && (
              <div className="text-muted text-sm">No chats yet. Flip the Chat toggle ON to receive seekers.</div>
            )}
            <div className="space-y-2">
              {seekerHistory.map((c) => (
                <button
                  key={c.chat_id}
                  onClick={() => openChat(c)}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${activeChat?.chat_id === c.chat_id ? "border-gold bg-[#FFB800]/5" : "border-soft hover:border-gold/40 hover:bg-amber-50/40"}`}
                  data-testid={`astro-chat-row-${c.chat_id}`}
                >
                  <div className="font-serif-display text-lg leading-tight">{c.customer_name}</div>
                  <div className="text-muted text-xs mt-0.5">
                    {c.started_at ? new Date(c.started_at).toLocaleString("en-IN", {
                      day: "numeric", month: "short", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    }) : "—"}
                  </div>
                  <div className="label-eyebrow mt-1 text-[10px]">
                    {c.status}
                    {typeof c.total_charged === "number" && c.total_charged > 0 && (
                      <span className="ml-2 text-emerald-700 normal-case tracking-normal">
                        · {INR(c.total_charged)} earned
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: active chat panel
              When the astrologer has just accepted a chat/call we promote
              this panel to a fixed full-screen overlay so they don't have
              to hunt for it on the dashboard. A "Minimize" button in the
              header collapses it back to the inline 8-col layout. */}
          <div
            className={
              chatFullscreen && activeChat
                ? "fixed inset-0 z-50 bg-[#FAF4E8] flex flex-col"
                : "lg:col-span-8 card-surface rounded-2xl flex flex-col"
            }
            style={chatFullscreen && activeChat ? undefined : { minHeight: "70vh" }}
            data-testid="active-chat-panel"
          >
            {!activeChat ? (
              <div className="flex-1 flex items-center justify-center text-muted text-sm px-6 text-center">
                Select a conversation from the left to read the seeker&apos;s chart context and reply.
              </div>
            ) : (
              <>
                <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-soft flex items-center justify-between gap-2 sm:gap-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-base sm:text-xl truncate">{activeChat.customer_name}</div>
                    <div className="label-eyebrow">{activeChat.status}</div>
                  </div>
                  {/* "User Kundali" pill — MOBILE ONLY. Hidden on md+ where
                      the full chart card sits inline below. Opens the
                      same full-featured SeekerChartModal that's used on
                      desktop (includes D1/D9 flip + 9-planet nakshatra
                      table + birth details + dasha summary), so the
                      mobile chat thread stays uncluttered without losing
                      any context the astrologer needs. */}
                  {context?.has_birth_details && (
                    <button
                      type="button"
                      onClick={() => setShowFullChart(true)}
                      className="md:hidden rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border border-amber-400/70 bg-amber-50 text-[#92400E] hover:bg-amber-100 transition flex-shrink-0 flex items-center gap-1.5"
                      data-testid="astro-open-seeker-sheet-btn"
                      aria-label="View seeker's Kundali"
                    >
                      <IdentificationCard size={12} weight="fill" />
                      User Kundali
                    </button>
                  )}
                  {chatFullscreen ? (
                    <button
                      type="button"
                      onClick={() => setChatFullscreen(false)}
                      className="rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border border-amber-400/70 bg-white text-[#92400E] hover:bg-amber-100 transition flex-shrink-0"
                      data-testid="astro-minimize-chat-btn"
                      aria-label="Minimize to dashboard"
                    >
                      ← Dashboard
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setChatFullscreen(true)}
                      className="rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border border-emerald-400/70 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition flex-shrink-0"
                      data-testid="astro-expand-chat-btn"
                      aria-label="Open full-screen chat"
                    >
                      Open Chat ↗
                    </button>
                  )}
                </div>

                {context && (
                  <div className="hidden md:block mx-4 mt-4 rounded-md border border-amber-300 bg-amber-50/60 px-4 py-3" data-testid="seeker-context-card">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="label-eyebrow flex items-center gap-1.5 !text-[#92400E]">
                        <IdentificationCard size={12} weight="fill" /> Seeker&apos;s chart
                      </p>
                      {!context.has_birth_details && (
                        <span className="text-[10px] text-rose-700 uppercase tracking-wider">Birth details pending</span>
                      )}
                    </div>
                    {context.has_birth_details ? (
                      <>
                        <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-xs text-[#3F2D1B]">
                          <div className="flex items-center gap-1.5"><CalendarBlank size={11} weight="fill" /> {context.birth_details?.date}</div>
                          <div className="flex items-center gap-1.5"><Clock size={11} weight="fill" /> {context.birth_details?.time} <span className="text-muted">({context.birth_details?.timezone})</span></div>
                          <div className="flex items-center gap-1.5 truncate"><MapPin size={11} weight="fill" /> <span className="truncate">{context.birth_details?.place}</span></div>
                        </div>

                        {/* North-Indian chart + Dasha CTAs sit at the top of the
                            card so the astrologer's eye finds them instantly. */}
                        <div className="mt-3 flex flex-col sm:flex-row gap-3 items-start">
                          {context.kundali?.lagna?.rashi && context.kundali?.planets && (
                            <button
                              type="button"
                              onClick={() => setShowFullChart(true)}
                              className="rounded-md border border-amber-300 bg-white shadow-sm hover:shadow-md transition p-1"
                              data-testid="open-seeker-chart-btn"
                              aria-label="Open full North-Indian chart"
                            >
                              <KundaliChart kundali={context.kundali} size={160} showToggle={false} />
                            </button>
                          )}
                          <div className="flex-1 min-w-0 space-y-2">
                            {context.kundali_summary && (
                              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <div><span className="text-muted">Lagna:</span> <span className="font-medium">{context.kundali_summary.lagna?.rashi} {context.kundali_summary.lagna?.degrees_in_rashi?.toFixed?.(1)}°</span></div>
                                <div><span className="text-muted">Moon:</span> <span className="font-medium">{context.kundali_summary.moon?.rashi} · {context.kundali_summary.moon?.nakshatra} pada {context.kundali_summary.moon?.pada}</span></div>
                                <div className="sm:col-span-2">
                                  <span className="text-muted">Dasha:</span>{" "}
                                  <span className="font-medium">
                                    {context.kundali_summary.current_mahadasha?.lord}
                                    {context.kundali_summary.current_antardasha?.lord ? <> &rarr; {context.kundali_summary.current_antardasha.lord}</> : null}
                                    {context.kundali_summary.current_pratyantar?.lord ? <> &rarr; {context.kundali_summary.current_pratyantar.lord}</> : null}
                                  </span>{" "}
                                  <span className="text-muted">
                                    (until {context.kundali_summary.current_pratyantar?.to || context.kundali_summary.current_antardasha?.to})
                                  </span>
                                </div>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setShowDashas(true)}
                              disabled={!(context.mahadashas && context.mahadashas.length)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/70 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#92400E] hover:bg-amber-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid="open-dasha-list-btn"
                            >
                              <ListBullets size={12} weight="bold" />
                              View dashas ({(context.mahadashas || []).length})
                            </button>
                          </div>
                        </div>

                        {context.kundali_summary?.d1_houses && Object.keys(context.kundali_summary.d1_houses).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-amber-200">
                            <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-0.5">D1 chart</p>
                            <div className="grid grid-cols-2 gap-x-3 text-[11px] font-mono leading-snug">
                              {Object.keys(context.kundali_summary.d1_houses)
                                .map((h) => parseInt(h, 10))
                                .sort((a, b) => a - b)
                                .map((h) => (
                                  <span key={h} className="truncate">
                                    H{h}: {context.kundali_summary.d1_houses[String(h)].join(", ")}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted mt-1.5">
                        This seeker hasn&apos;t completed onboarding yet. They&apos;ll be prompted on next login.
                      </p>
                    )}
                  </div>
                )}

                {/* MOBILE-only "User Kundali" pill in the chat header above
                    opens the full SeekerChartModal directly — no separate
                    custom sheet needed. The modal already has D1/D9 flip,
                    9-planet nakshatras, birth details, and dasha summary. */}

                {endedBySeeker && (
                  <div
                    className="mx-4 mt-4 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2"
                    data-testid="seeker-ended-banner"
                  >
                    <Warning size={16} weight="fill" className="text-rose-600 mt-0.5" />
                    <div>
                      <p className="font-semibold">{activeChat.customer_name} ended this chat.</p>
                      <p className="text-xs text-rose-700/80 mt-0.5">
                        Replies are disabled. The conversation has been saved to your history.
                      </p>
                    </div>
                  </div>
                )}

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
                  {priorChatsMeta.length > 0 && (
                    <div
                      className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-center justify-between gap-2"
                      data-testid="prior-chats-strip"
                    >
                      <span className="font-semibold">📜 Returning seeker</span>
                      <span className="text-amber-700">
                        {priorChatsMeta.length} prior session{priorChatsMeta.length === 1 ? "" : "s"} loaded above
                      </span>
                    </div>
                  )}
                  {messages.map((m) => {
                    // Synthetic divider between prior conversations + today's session.
                    if (m.sender_role === "system" || m.is_divider) {
                      return (
                        <div key={m.message_id} className="flex justify-center my-3" data-testid="prior-divider">
                          <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            {m.text}
                          </span>
                        </div>
                      );
                    }
                    const isMine = m.sender_role === "astrologer" || m.sender_role === "ai";
                    // Prior-conversation messages dimmed so today's session reads clearly.
                    // We detect by chat_id !== current chat_id.
                    const isPrior = m.chat_id && activeChat?.chat_id && m.chat_id !== activeChat.chat_id;
                    return (
                      <div key={m.message_id} className={`flex ${isMine ? "justify-end" : "justify-start"} ${isPrior ? "opacity-65" : ""}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${isMine ? "bg-[#FFB800] text-[#1A1410] rounded-br-sm" : "bg-[#FAF4E8] text-[#1A1410] border border-soft rounded-bl-sm"}`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })}
                  {seekerTyping && (
                    <div className="flex justify-start" data-testid="seeker-typing">
                      <div className="bg-[#FAF4E8] text-[#1A1410] border border-soft rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" />
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "0.12s" }} />
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "0.24s" }} />
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-500">typing</span>
                      </div>
                    </div>
                  )}
                </div>
                <form onSubmit={send} className="border-t border-soft px-4 py-3 flex items-center gap-2">
                  <input
                    value={text}
                    onChange={(e) => { setText(e.target.value); if (e.target.value) pingAstroTyping(); }}
                    placeholder={
                      activeChat.status === "ended" || endedBySeeker
                        ? "Send follow-up note (chat ended — no billing)…"
                        : "Reply..."
                    }
                    className="flex-1 bg-transparent outline-none px-3 py-2 text-sm border border-soft rounded-full focus:border-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="astro-chat-input"
                  />
                  <button
                    type="submit"
                    className="btn-primary rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="astro-send-btn"
                    title={activeChat.status === "ended" || endedBySeeker ? "Send a follow-up note (chat is paused — no billing)" : "Send"}
                  >
                    <PaperPlaneTilt size={18} weight="fill" />
                  </button>
                  {/* End-chat button (astrologer-side). Hidden once the chat is
                      ended (by either side). Runs the same final settle-up
                      as the seeker-side end button + advances the queue. */}
                  {activeChat.status !== "ended" && !endedBySeeker && (
                    <button
                      type="button"
                      onClick={endChatFromAstro}
                      disabled={endingChat}
                      title="End this chat"
                      aria-label="End chat"
                      className="rounded-full w-11 h-11 flex items-center justify-center bg-red-50 hover:bg-red-100 border border-red-300 text-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      data-testid="astro-end-chat-btn"
                    >
                      <PhoneSlash size={18} weight="fill" />
                    </button>
                  )}
                </form>
              </>
            )}
          </div>
        </div>

        {/* ─── Reviews ─── */}
        <div className="mt-10" data-testid="reviews-section">          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl sm:text-3xl">What seekers said</h2>
            <span className="label-eyebrow">{reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
          </div>
          {reviews.length === 0 ? (
            <div className="card-surface rounded-2xl p-8 text-center text-muted text-sm">
              <Sparkle size={18} weight="duotone" className="text-gold mx-auto mb-2" />
              No reviews yet. Your first 5-star is one chat away.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reviews.map((r) => (
                <article key={r.review_id} className="card-surface rounded-2xl p-5" data-testid={`review-card-${r.review_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gold">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} size={14} weight={n <= r.rating ? "fill" : "regular"} className={n <= r.rating ? "text-gold" : "text-stone-300"} />
                      ))}
                    </div>
                    <span className="text-[10px] text-muted">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </span>
                  </div>
                  <p className="font-serif-display text-base mt-3 leading-snug text-[#3F2D1B]">&ldquo;{r.comment || "No comment left."}&rdquo;</p>
                  <p className="text-xs text-muted mt-3">— {r.customer_name}</p>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* ─── Withdrawal modal ─── */}
        {showWithdraw && (
          <WithdrawModal
            available={stats.wallet_balance}
            rate={astro.rate_per_min}
            payoutAccount={stats.payout_account}
            onClose={() => setShowWithdraw(false)}
            onSuccess={async () => {
              setShowWithdraw(false);
              await refresh();
            }}
          />
        )}

        {/* ─── Payout-account editor ─── */}
        {showPayoutEditor && (
          <PayoutAccountModal
            existing={stats.payout_account}
            onClose={() => setShowPayoutEditor(false)}
            onSaved={async () => {
              setShowPayoutEditor(false);
              await refresh();
            }}
          />
        )}

        {/* ─── Incoming WebRTC call ring ─── */}
        {voice.active && voice.active.direction === "in" && (
          <IncomingCallModal
            voice={voice}
            astrologer={astro}
            activeChatContext={context}
            activeChatCustomerName={activeChat?.customer_name}
            onOpenChart={() => setShowFullChart(true)}
            onOpenDashas={() => setShowDashas(true)}
          />
        )}

        {/* ─── Incoming CHAT ring (with audio beep loop) ─── */}
        {incoming && (
          <IncomingChatModal
            chat={incoming}
            context={incomingContext}
            onAccept={acceptIncomingChat}
            onDecline={declineIncomingChat}
          />
        )}

        {/* ─── Incoming CALL banner (PSTN / WebRTC heads-up + chart) ─── */}
        {incomingCall && (
          <IncomingCallBanner
            call={incomingCall}
            onDismiss={() => setDismissedCallIds((s) => new Set(s).add(incomingCall.call_id))}
          />
        )}

        {/* ─── Full-screen Seeker Chart modal ─── */}
        {showFullChart && context?.kundali?.lagna?.rashi && (
          <SeekerChartModal
            context={context}
            customerName={activeChat?.customer_name}
            onClose={() => setShowFullChart(false)}
            onOpenDashas={() => { setShowFullChart(false); setShowDashas(true); }}
          />
        )}

        {/* ─── Dasha list modal — all 9 Vimshottari Mahadashas ─── */}
        {showDashas && (
          <DashaListModal
            mahadashas={context?.mahadashas || []}
            currentLord={context?.kundali_summary?.current_mahadasha?.lord}
            customerName={activeChat?.customer_name}
            onClose={() => setShowDashas(false)}
          />
        )}

        {/* MOBILE-only floating CTA — surfaces when there's a live chat
            but the astrologer has minimized it to scroll the dashboard.
            One tap re-enters the full-screen takeover so they never
            "lose" the conversation. Auto-hides on md+ (where the chat
            panel is always visible inline) and when the chat is already
            full-screen. */}
        {activeChat && !chatFullscreen && activeChat.status === "active" && (
          <button
            type="button"
            onClick={() => setChatFullscreen(true)}
            className="md:hidden fixed bottom-5 right-5 z-40 rounded-full bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white px-5 py-3 text-xs font-extrabold uppercase tracking-wider shadow-2xl flex items-center gap-2 border-2 border-white"
            data-testid="astro-return-to-chat-fab"
            aria-label="Return to active chat"
          >
            <ChatTeardropDots size={16} weight="fill" />
            Return to chat
          </button>
        )}
      </div>
    </PageShell>
  );
}

/* ─── Subcomponents ────────────────────────────────────────────── */

function AvailabilityCard({ label, description, icon: Icon, on, busy, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => !busy && onChange(!on)}
      className={`w-full text-left rounded-2xl p-5 sm:p-6 border-2 transition-all ${
        on
          ? "border-emerald-500 shadow-lg shadow-emerald-500/20"
          : "border-soft hover:border-gold/50"
      }`}
      style={{
        // Solid colour overrides — survive the cosmic-night CSS-variable
        // re-theming AND look right in the default Royal-Amber theme.
        backgroundColor: on ? "#059669" : undefined,
        color: on ? "#ffffff" : undefined,
        backgroundImage: on
          ? "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)"
          : undefined,
      }}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div className={`rounded-full p-3 ${on ? "bg-white/20 text-white" : "bg-stone-200 text-stone-500"}`}>
          <Icon size={22} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={`font-serif-display text-lg sm:text-xl ${on ? "text-white" : "text-[#3F2D1B]"}`} style={on ? { color: "#ffffff" } : undefined}>
              {label}
            </p>
            <span
              role="switch"
              aria-checked={on}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                on ? "bg-white/30 ring-2 ring-white/60" : "bg-stone-300"
              }`}
              data-testid={`${testid}-switch`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  on ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </div>
          <p
            className={`text-xs sm:text-sm mt-1 ${on ? "text-white/95" : "text-muted"}`}
            style={on ? { color: "rgba(255,255,255,0.95)" } : undefined}
          >
            {busy ? "Saving…" : description}
          </p>
          {on && (
            <span
              className="inline-flex items-center gap-1.5 mt-3 text-[10px] uppercase tracking-widest font-bold"
              style={{ color: "#fef3c7" }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-300" />
              </span>
              Live now
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function RevenueStat({ label, value, testid }) {
  return (
    <div className="card-surface rounded-2xl p-4 sm:p-5" data-testid={testid}>
      <p className="label-eyebrow !text-[10px]">{label}</p>
      <p className="font-serif-display text-2xl sm:text-3xl mt-2 text-[#3F2D1B]">
        {INR(value)}
      </p>
    </div>
  );
}

function WithdrawModal({ available, rate, payoutAccount, onClose, onSuccess }) {
  const [mode, setMode] = useState("full"); // "full" | "custom"
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // success state — when set, shows the 7-day confirmation

  const destination = useMemo(() => {
    if (!payoutAccount) return "your registered account";
    if (payoutAccount.method === "upi") return `UPI ${payoutAccount.upi_vpa}`;
    if (payoutAccount.method === "bank") return `${payoutAccount.account_number_masked} · ${payoutAccount.ifsc}`;
    return "your registered account";
  }, [payoutAccount]);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    let payload = { full_amount: true };
    if (mode === "custom") {
      const n = parseFloat(amount);
      if (!n || n <= 0) {
        toast.error("Enter a valid amount");
        return;
      }
      if (n > available + 0.01) {
        toast.error(`Max withdrawable is ${INR(available)}`);
        return;
      }
      payload = { amount: n, full_amount: false };
    }
    setBusy(true);
    try {
      const { data } = await api.post("/astrologer/withdrawals", payload);
      setDone({ amount: data.amount, message: data.message });
      toast.success("Withdrawal request submitted!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit withdrawal. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Backdrop click closes only when not busy and not in success state
  const onBackdrop = () => { if (!busy) (done ? onSuccess() : onClose()); };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="withdraw-modal"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onBackdrop}
        data-testid="withdraw-backdrop"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 p-6 sm:p-7">
        <button
          type="button"
          onClick={onBackdrop}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
          aria-label="Close"
          data-testid="withdraw-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-3" data-testid="withdraw-success">
            <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Sparkle size={26} weight="fill" className="text-emerald-600" />
            </div>
            <h2 className="font-serif-display text-2xl text-[#3F2D1B]">You&rsquo;re all set!</h2>
            <p className="text-sm text-stone-700 mt-3 leading-relaxed">
              Your withdrawal request of{" "}
              <span className="font-bold text-[#3F2D1B]">{INR(done.amount)}</span> has been
              received. You&rsquo;ll receive the amount at{" "}
              <span className="font-bold text-[#3F2D1B]" data-testid="success-destination">{destination}</span>{" "}
              within the next <span className="font-bold">7 days</span>.
            </p>
            <p className="text-xs text-muted mt-2">
              You can track its status under your wallet anytime.
            </p>
            <button
              type="button"
              onClick={onSuccess}
              className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium mt-6"
              data-testid="withdraw-done-btn"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <p className="label-eyebrow">Withdraw balance</p>
              <h2 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mt-1">
                Cash out your earnings
              </h2>
              <p className="text-sm text-muted mt-2">
                Available right now:{" "}
                <span className="font-bold text-[#3F2D1B]" data-testid="withdraw-available">
                  {INR(available)}
                </span>
              </p>
              {payoutAccount && (
                <p className="text-xs text-stone-600 mt-2 flex items-center gap-1.5" data-testid="withdraw-destination">
                  <ShieldCheck size={12} weight="fill" className="text-emerald-600" />
                  Sending to <span className="font-medium text-[#3F2D1B]">{destination}</span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode("full")}
                className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                  mode === "full"
                    ? "border-amber-500 bg-amber-50"
                    : "border-stone-200 hover:border-amber-300"
                }`}
                data-testid="withdraw-mode-full"
              >
                <p className="text-xs font-semibold tracking-wide uppercase text-amber-700">Full</p>
                <p className="font-serif-display text-lg mt-0.5 text-[#3F2D1B]">{INR(available)}</p>
              </button>
              <button
                type="button"
                onClick={() => setMode("custom")}
                className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                  mode === "custom"
                    ? "border-amber-500 bg-amber-50"
                    : "border-stone-200 hover:border-amber-300"
                }`}
                data-testid="withdraw-mode-custom"
              >
                <p className="text-xs font-semibold tracking-wide uppercase text-amber-700">Custom</p>
                <p className="text-xs text-stone-600 mt-0.5">Type any amount</p>
              </button>
            </div>

            {mode === "custom" && (
              <div>
                <label className="label-eyebrow">Amount (₹)</label>
                <div className="mt-2 flex items-center border border-stone-300 rounded-md focus-within:border-gold transition-colors">
                  <span className="px-3 text-stone-500">₹</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    max={available}
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Up to ${available}`}
                    className="flex-1 px-1 py-2.5 outline-none text-sm bg-transparent"
                    autoFocus
                    data-testid="withdraw-custom-input"
                  />
                </div>
                <p className="text-[11px] text-muted mt-1.5">
                  Min ₹1 · Max {INR(available)}
                </p>
              </div>
            )}

            <div className="rounded-md bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600 leading-relaxed">
              You&rsquo;ll receive the requested amount in your registered bank/UPI account within{" "}
              <span className="font-semibold text-[#3F2D1B]">7 days</span>. Earned at{" "}
              <span className="font-semibold text-[#3F2D1B]">{INR(rate)}/min</span>.
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="flex-1 btn-ghost rounded-full py-3 text-sm"
                data-testid="withdraw-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || (mode === "custom" && (!amount || Number(amount) <= 0))}
                className="flex-1 btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-50"
                data-testid="withdraw-submit"
              >
                {busy ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PayoutPill({ account, onEdit }) {
  if (!account) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="mt-4 w-full inline-flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-xs bg-rose-500/15 ring-1 ring-rose-400/40 text-amber-100 hover:bg-rose-500/25 transition"
        data-testid="payout-add-cta"
      >
        <span className="flex items-center gap-2">
          <Warning size={14} weight="fill" className="text-rose-300" />
          <span className="font-medium">No payout method on file</span>
        </span>
        <span className="text-[10px] uppercase tracking-widest font-bold text-rose-200">Add UPI / Bank</span>
      </button>
    );
  }
  const isUpi = account.method === "upi";
  return (
    <button
      type="button"
      onClick={onEdit}
      className="mt-4 w-full inline-flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-xs bg-emerald-500/15 ring-1 ring-emerald-400/30 text-amber-100 hover:bg-emerald-500/25 transition group"
      data-testid="payout-pill"
    >
      <span className="flex items-center gap-2 min-w-0">
        {isUpi
          ? <ShieldCheck size={14} weight="fill" className="text-emerald-300 shrink-0" />
          : <Bank size={14} weight="fill" className="text-emerald-300 shrink-0" />}
        <span className="truncate">
          <span className="text-[10px] uppercase tracking-widest text-emerald-200/80 mr-1.5">
            {isUpi ? "UPI" : "Bank"}
          </span>
          <span className="font-medium" data-testid="payout-display">{account.display}</span>
        </span>
      </span>
      <PencilSimple size={12} className="text-amber-100/60 group-hover:text-amber-100 shrink-0" />
    </button>
  );
}

function PayoutAccountModal({ existing, onClose, onSaved }) {
  const [method, setMethod] = useState(existing?.method || "upi");
  const [upi, setUpi] = useState(existing?.upi_vpa || "");
  const [holder, setHolder] = useState(existing?.account_holder || "");
  const [acctNum, setAcctNum] = useState("");
  const [ifsc, setIfsc] = useState(existing?.ifsc || "");
  const [bank, setBank] = useState(existing?.bank_name || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = method === "upi"
        ? { method: "upi", upi_vpa: upi.trim() }
        : { method: "bank", account_holder: holder.trim(), account_number: acctNum.trim(), ifsc: ifsc.trim(), bank_name: bank.trim() };
      await api.put("/astrologer/payout-account", payload);
      toast.success("Payout method saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save. Check details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="payout-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 p-6 sm:p-7 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
          data-testid="payout-close"
        >
          <X size={18} />
        </button>

        <p className="label-eyebrow">Payout method</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mt-1">
          {existing ? "Update where money lands" : "Where should we send your earnings?"}
        </h2>
        <p className="text-sm text-muted mt-2">
          We need this on file before any withdrawal — so the 7-day promise is one we can keep.
        </p>

        <form onSubmit={submit} className="space-y-5 mt-5">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMethod("upi")}
              className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                method === "upi" ? "border-amber-500 bg-amber-50" : "border-stone-200 hover:border-amber-300"
              }`}
              data-testid="payout-method-upi"
            >
              <p className="text-xs font-semibold tracking-wide uppercase text-amber-700">UPI</p>
              <p className="text-xs text-stone-600 mt-0.5">Instant · recommended</p>
            </button>
            <button
              type="button"
              onClick={() => setMethod("bank")}
              className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                method === "bank" ? "border-amber-500 bg-amber-50" : "border-stone-200 hover:border-amber-300"
              }`}
              data-testid="payout-method-bank"
            >
              <p className="text-xs font-semibold tracking-wide uppercase text-amber-700">Bank</p>
              <p className="text-xs text-stone-600 mt-0.5">NEFT / IMPS</p>
            </button>
          </div>

          {method === "upi" ? (
            <div>
              <label className="label-eyebrow">UPI ID</label>
              <input
                type="text"
                value={upi}
                onChange={(e) => setUpi(e.target.value)}
                placeholder="name@hdfcbank"
                autoFocus
                autoComplete="off"
                className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
                data-testid="payout-upi-input"
              />
              <p className="text-[11px] text-muted mt-1.5">Format: <span className="font-mono">name@bank</span> (Paytm, GPay, PhonePe etc).</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow">Account holder name</label>
                <input
                  type="text"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  placeholder="As printed on the passbook"
                  className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
                  data-testid="payout-bank-holder"
                />
              </div>
              <div>
                <label className="label-eyebrow">Account number</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={acctNum}
                  onChange={(e) => setAcctNum(e.target.value.replace(/\D/g, ""))}
                  placeholder={existing?.method === "bank" && !acctNum ? `Replaces ${existing.account_number_masked}` : "6–20 digits"}
                  maxLength={20}
                  autoComplete="off"
                  className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none font-mono"
                  data-testid="payout-bank-number"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow">IFSC</label>
                  <input
                    type="text"
                    value={ifsc}
                    onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                    placeholder="HDFC0001234"
                    maxLength={11}
                    className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none font-mono uppercase"
                    data-testid="payout-bank-ifsc"
                  />
                </div>
                <div>
                  <label className="label-eyebrow">Bank (optional)</label>
                  <input
                    type="text"
                    value={bank}
                    onChange={(e) => setBank(e.target.value)}
                    placeholder="HDFC Bank"
                    className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
                    data-testid="payout-bank-name"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="rounded-md bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600 leading-relaxed">
            <span className="font-semibold text-[#3F2D1B]">Privacy:</span> we mask your bank
            number on every screen — only the last 4 digits are ever visible to admins.
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 btn-ghost rounded-full py-3 text-sm"
              data-testid="payout-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || (method === "upi" ? !upi.trim() : !(holder.trim() && acctNum.trim() && ifsc.trim()))}
              className="flex-1 btn-primary rounded-full py-3 text-sm font-medium disabled:opacity-50"
              data-testid="payout-save"
            >
              {busy ? "Saving…" : "Save payout method"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}



function PhoneNumberRow({ existing, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(existing || "");
  const [busy, setBusy] = useState(false);

  // Reset local state when the saved number changes externally
  useEffect(() => { setPhone(existing || ""); }, [existing]);

  const save = async (e) => {
    e?.preventDefault();
    if (busy) return;
    const cleaned = phone.trim().replace(/\s/g, "");
    if (cleaned && !/^\+[1-9]\d{6,15}$/.test(cleaned)) {
      toast.error("Phone must be in international format (e.g. +919876543210)");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/astrologer/me", { phone: cleaned });
      toast.success(cleaned ? "Phone saved — seekers can now bridge calls to it." : "Phone removed");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save phone.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 card-surface rounded-2xl p-4 sm:p-5" data-testid="calling-phone-row">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="rounded-full p-2.5 bg-amber-100 text-amber-700">
            <Phone size={18} weight="fill" />
          </div>
          <div className="min-w-0">
            <p className="font-serif-display text-base sm:text-lg text-[#3F2D1B]">Phone for PSTN bridge</p>
            {existing && !editing ? (
              <p className="text-sm text-stone-700 mt-0.5 font-mono" data-testid="phone-display">
                {existing}{" "}
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 ml-1">
                  <ShieldCheck size={11} weight="fill" /> Verified
                </span>
              </p>
            ) : (
              <p className="text-xs text-muted mt-0.5">
                {existing
                  ? "Update your number — seekers will reach this when they pick the Phone-call option."
                  : "Add a number so seekers in India can reach you on a regular phone call (instead of in-app)."}
              </p>
            )}
          </div>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-1.5 self-start sm:self-auto"
            data-testid="phone-edit-btn"
          >
            <PencilSimple size={12} /> {existing ? "Edit" : "Add phone"}
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            autoFocus
            className="flex-1 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none font-mono"
            data-testid="phone-input"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setEditing(false); setPhone(existing || ""); }}
              disabled={busy}
              className="btn-ghost rounded-full px-4 py-2.5 text-sm"
              data-testid="phone-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50"
              data-testid="phone-save-btn"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function IncomingChatModal({ chat, context, onAccept, onDecline }) {
  const ks = context?.kundali_summary;
  const bd = context?.birth_details;
  const md = ks?.current_mahadasha;
  const ad = ks?.current_antardasha;
  const pd = ks?.current_pratyantar;
  const houses = ks?.d1_houses || {};
  const houseRows = Object.keys(houses)
    .map((h) => parseInt(h, 10))
    .sort((a, b) => a - b)
    .map((h) => `H${h}: ${houses[String(h)].join(", ")}`);

  return (
    <div
      className="fixed inset-0 z-[120] bg-gradient-to-br from-[#3F2D1B] via-[#2A1F12] to-[#1A1410] flex flex-col items-center px-6 pt-8 pb-32 overflow-y-auto"
      data-testid="incoming-chat-modal"
    >
      <div className="absolute top-4 right-4 text-amber-300/60 text-xs font-mono uppercase tracking-widest">
        Incoming
      </div>

      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-amber-500/20 flex items-center justify-center border-4 border-amber-300/60 shadow-2xl animate-pulse">
        <ChatCircleDots size={40} weight="fill" className="text-amber-300" />
      </div>
      <p className="label-eyebrow !text-amber-300 mt-4">New chat from a seeker</p>
      <h2 className="font-serif-display text-2xl sm:text-3xl text-amber-100 mt-1" data-testid="incoming-chat-name">
        💬 {chat.customer_name}
      </h2>

      {/* Chart-at-a-glance card — birth, lagna+moon+dashas, D1 houses.
          Loads asynchronously; modal stays usable while we fetch.
          Inline styles force dark-on-cream regardless of cosmic-night
          theme overrides (which would otherwise blank out the card). */}
      {context && bd?.date && (
        <div
          className="mt-5 w-full max-w-md rounded-xl p-4 shadow-2xl ring-1"
          style={{ backgroundColor: "#FFFBF5", color: "#1A1410", borderColor: "#FCD7A0" }}
          data-testid="incoming-chart-card"
        >
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold" style={{ color: "#92400E" }}>
            <span>Quick chart</span>
            <span style={{ color: "#6B7280", textTransform: "none", letterSpacing: "normal", fontFamily: "monospace" }}>
              {bd.date} · {bd.time} · {bd.timezone}
            </span>
          </div>
          <p className="text-xs mt-1 truncate" style={{ color: "#374151" }}>{bd.place}</p>

          {ks && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-xs" style={{ color: "#1A1410" }}>
              <div>
                <span style={{ color: "#6B7280" }}>Lagna:</span>{" "}
                <span className="font-semibold" style={{ color: "#1A1410" }}>{ks.lagna?.rashi}</span>
              </div>
              <div>
                <span style={{ color: "#6B7280" }}>Moon:</span>{" "}
                <span className="font-semibold" style={{ color: "#1A1410" }}>{ks.moon?.rashi}</span>{" "}
                <span style={{ color: "#6B7280" }}>·</span>{" "}
                <span style={{ color: "#1A1410" }}>{ks.moon?.nakshatra}</span>
              </div>
            </div>
          )}

          {(md || ad || pd) && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid #FCD7A0" }} data-testid="incoming-dasha-line">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#92400E" }}>
                Current dasha
              </p>
              <p className="text-sm font-mono leading-relaxed" style={{ color: "#1A1410" }}>
                {md?.lord ? <span className="font-bold" style={{ color: "#1A1410" }}>{md.lord}</span> : <span style={{ color: "#1A1410" }}>?</span>}
                {ad ? <span style={{ color: "#6B7280" }}> &rarr; </span> : null}
                {ad?.lord ? <span className="font-semibold" style={{ color: "#1A1410" }}>{ad.lord}</span> : null}
                {pd ? <span style={{ color: "#6B7280" }}> &rarr; </span> : null}
                {pd?.lord ? <span style={{ color: "#1A1410" }}>{pd.lord}</span> : null}
              </p>
              {pd?.to && (
                <p className="text-[10px] mt-0.5" style={{ color: "#6B7280" }}>
                  Pratyantar runs until {pd.to}
                </p>
              )}
            </div>
          )}

          {houseRows.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid #FCD7A0" }} data-testid="incoming-d1-houses">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#92400E" }}>
                D1 chart
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono leading-snug" style={{ color: "#1A1410" }}>
                {houseRows.map((row) => (
                  <span key={row} className="truncate" style={{ color: "#1A1410" }}>{row}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {!context && (
        <p className="text-amber-200/60 text-xs mt-4 italic">Loading their chart…</p>
      )}

      {/* Accept / Decline — sticky bottom bar so they're always visible
          on mobile even when the chart/dasha card is scrolled. Sits on
          top of a subtle gradient overlay for visibility against any
          background content. pb-safe respects the home-indicator inset. */}
      <div
        className="fixed left-0 right-0 bottom-0 z-[121] flex items-center justify-center gap-6 px-6 pt-6"
        style={{
          paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
          background: "linear-gradient(to top, #1A1410 60%, rgba(26,20,16,0.85) 88%, rgba(26,20,16,0))",
        }}
      >
        <button
          onClick={onDecline}
          className="w-14 h-14 rounded-full bg-rose-600 hover:bg-rose-700 active:scale-95 text-white flex items-center justify-center shadow-2xl"
          data-testid="incoming-chat-decline-btn"
          aria-label="Decline"
        >
          <X size={22} weight="bold" />
        </button>
        <button
          onClick={onAccept}
          className="px-7 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white flex items-center gap-2 shadow-2xl animate-pulse font-semibold"
          data-testid="incoming-chat-accept-btn"
          aria-label="Accept"
        >
          <ChatCircleDots size={20} weight="fill" /> Accept
        </button>
      </div>
    </div>
  );
}

function IncomingCallModal({ voice, astrologer, activeChatContext, activeChatCustomerName, onOpenChart, onOpenDashas }) {
  const [accepted, setAccepted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  // Auto-tick once accepted so the astrologer sees their own call timer.
  useEffect(() => {
    if (!accepted) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [accepted]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const onAccept = () => {
    voice.accept();
    setAccepted(true);
  };
  const onDecline = () => voice.reject();
  const onHangup = () => voice.hangup();

  const hasChart = !!(activeChatContext?.kundali?.lagna?.rashi && activeChatContext?.kundali?.planets);
  const hasDashas = !!(activeChatContext?.mahadashas && activeChatContext.mahadashas.length);

  return (
    <div
      className="fixed inset-0 z-[120] bg-gradient-to-br from-[#3F2D1B] via-[#2A1F12] to-[#1A1410] flex flex-col items-center justify-center px-6"
      data-testid="incoming-call-modal"
    >
      <div className="absolute top-4 right-4 text-amber-300/60 text-xs font-mono">
        {accepted ? fmt(seconds) : "Incoming"}
      </div>
      <img
        src={astrologer.avatar}
        alt=""
        className={`w-32 h-32 rounded-full object-cover border-4 border-amber-300/60 shadow-2xl ${accepted ? "" : "animate-pulse"}`}
      />
      <p className="label-eyebrow !text-amber-300 mt-6">
        {accepted ? "Connected" : "Incoming call from a seeker"}
      </p>
      <h2 className="font-serif-display text-3xl text-amber-100 mt-2">
        {accepted ? `${fmt(seconds)} · ₹${astrologer.rate_per_min}/min` : "📞 New consultation"}
      </h2>

      {!accepted ? (
        <div className="flex items-center gap-6 mt-12">
          <button
            onClick={onDecline}
            className="w-16 h-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-2xl"
            data-testid="incoming-decline-btn"
            aria-label="Decline"
          >
            <PhoneSlash size={26} weight="fill" />
          </button>
          <button
            onClick={onAccept}
            className="w-16 h-16 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center shadow-2xl animate-pulse"
            data-testid="incoming-accept-btn"
            aria-label="Accept"
          >
            <Phone size={26} weight="fill" />
          </button>
        </div>
      ) : (
        <>
          {/* Once the astrologer picks up, surface the seeker's chart +
              dashas as one-tap CTAs. Sourced from the chat currently
              open on the dashboard (most-recent seeker the astrologer
              was reading). Hidden if no chat context is loaded. */}
          {(hasChart || hasDashas) && (
            <div className="mt-6 w-full max-w-md rounded-xl border border-amber-300/40 bg-white/5 backdrop-blur px-4 py-3" data-testid="call-seeker-context">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80">
                {activeChatCustomerName ? `${activeChatCustomerName}'s chart` : "Seeker's chart"}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {hasChart && (
                  <button
                    type="button"
                    onClick={onOpenChart}
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 text-[#1A1410] px-3 py-1.5 text-xs font-semibold hover:brightness-105"
                    data-testid="call-open-chart-btn"
                  >
                    <IdentificationCard size={13} weight="fill" /> View chart
                  </button>
                )}
                {hasDashas && (
                  <button
                    type="button"
                    onClick={onOpenDashas}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/70 text-amber-100 px-3 py-1.5 text-xs font-semibold hover:bg-amber-300/10"
                    data-testid="call-open-dashas-btn"
                  >
                    <ListBullets size={13} weight="bold" /> View dashas
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-5 mt-8">
            <button
              onClick={voice.toggleMute}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition ${voice.muted ? "bg-amber-300 text-[#1A1410]" : "bg-white/10 hover:bg-white/20 text-amber-100"}`}
              data-testid="incoming-mute-btn"
              aria-label={voice.muted ? "Unmute" : "Mute"}
            >
              {voice.muted ? <MicrophoneSlash size={22} weight="fill" /> : <Microphone size={22} weight="fill" />}
            </button>
            <button
              onClick={onHangup}
              className="w-16 h-16 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-2xl"
              data-testid="incoming-hangup-btn"
              aria-label="Hang up"
            >
              <PhoneSlash size={26} weight="fill" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}


/* ─── Seeker Chart Modal (full North-Indian chart) ─────────────── */
function SeekerChartModal({ context, customerName, onClose, onOpenDashas }) {
  // D1 (Lagna / Rashi) is the default view; D9 (Navamsa) is shown on tap.
  // Falls back to D1 silently if D9 failed to compute on the backend.
  const hasNavamsa = !!(context?.navamsa?.lagna?.rashi && context?.navamsa?.planets);
  const [view, setView] = useState("d1");
  const activeChart =
    view === "d9" && hasNavamsa ? context.navamsa : context.kundali;
  const activeLabel = view === "d9" ? "NAVAMSA" : "LAGNA";
  const planetNak = context?.planet_nakshatras || [];
  const dashaCount = (context?.mahadashas || []).length;

  return (
    <div
      className="fixed inset-0 z-[125] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="seeker-chart-modal"
    >
      <div
        className="relative rounded-2xl p-5 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ backgroundColor: "#FFFBF5", color: "#1A1410" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-1.5 hover:bg-amber-100"
          aria-label="Close"
          data-testid="close-chart-modal-btn"
        >
          <X size={18} weight="bold" style={{ color: "#92400E" }} />
        </button>
        <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#92400E" }}>
          North-Indian chart
        </p>
        <h3 className="font-serif-display text-xl mt-0.5" style={{ color: "#1A1410" }}>
          {customerName || "Seeker"}
        </h3>
        <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
          {context?.birth_details?.date} · {context?.birth_details?.time} ·{" "}
          {context?.birth_details?.place}
        </p>

        {/* Quick action: open the full Vimshottari dasha timeline modal.
            Mobile-critical — without this, astrologers on the User Kundali
            sheet had no way to drill into Mahadasha → Antardasha → PD. */}
        {dashaCount > 0 && onOpenDashas && (
          <button
            type="button"
            onClick={onOpenDashas}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider shadow"
            style={{ backgroundColor: "#92400E", color: "#FFFBF5" }}
            data-testid="open-dasha-list-from-chart-btn"
          >
            <ListBullets size={14} weight="bold" />
            View {dashaCount} Vimshottari Dashas
          </button>
        )}

        {/* D1 / D9 tab switcher */}
        <div
          className="mt-3 inline-flex rounded-full p-0.5 text-xs"
          style={{ backgroundColor: "#FEF3C7" }}
          data-testid="chart-divisional-tabs"
        >
          <button
            type="button"
            onClick={() => setView("d1")}
            className="px-3 py-1 rounded-full font-semibold transition-colors"
            style={{
              backgroundColor: view === "d1" ? "#92400E" : "transparent",
              color: view === "d1" ? "#FFFBF5" : "#92400E",
            }}
            data-testid="chart-tab-d1"
          >
            D1 · Lagna
          </button>
          <button
            type="button"
            onClick={() => hasNavamsa && setView("d9")}
            disabled={!hasNavamsa}
            className="px-3 py-1 rounded-full font-semibold transition-colors disabled:opacity-40"
            style={{
              backgroundColor: view === "d9" ? "#92400E" : "transparent",
              color: view === "d9" ? "#FFFBF5" : "#92400E",
            }}
            data-testid="chart-tab-d9"
            title={hasNavamsa ? "Navamsa (D9)" : "D9 unavailable"}
          >
            D9 · Navamsa
          </button>
        </div>

        <div className="mt-3 flex items-center justify-center">
          <KundaliChart kundali={activeChart} size={300} label={activeLabel} />
        </div>

        {context?.kundali_summary && view === "d1" && (
          <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs" style={{ color: "#1A1410" }}>
            <div>
              <span style={{ color: "#6B7280" }}>Lagna:</span>{" "}
              <span className="font-semibold" style={{ color: "#1A1410" }}>{context.kundali_summary.lagna?.rashi}</span>
            </div>
            <div>
              <span style={{ color: "#6B7280" }}>Moon:</span>{" "}
              <span className="font-semibold" style={{ color: "#1A1410" }}>{context.kundali_summary.moon?.rashi}</span>
              {" · "}
              <span style={{ color: "#1A1410" }}>{context.kundali_summary.moon?.nakshatra}</span>
            </div>
          </div>
        )}

        {/* 9-planet Nakshatra + Pada table */}
        {planetNak.length > 0 && (
          <div className="mt-5" data-testid="planet-nakshatra-table">
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: "#92400E" }}>
              Planets · Nakshatra &amp; Pada
            </p>
            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "#FDE68A" }}>
              <table className="w-full text-[11px]" style={{ color: "#1A1410" }}>
                <thead style={{ backgroundColor: "#FEF3C7" }}>
                  <tr>
                    <th className="text-left px-2 py-1 font-semibold" style={{ color: "#92400E" }}>Planet</th>
                    <th className="text-left px-2 py-1 font-semibold" style={{ color: "#92400E" }}>Rashi</th>
                    <th className="text-left px-2 py-1 font-semibold" style={{ color: "#92400E" }}>Nakshatra</th>
                    <th className="text-center px-2 py-1 font-semibold" style={{ color: "#92400E" }}>Pada</th>
                    <th className="text-left px-2 py-1 font-semibold" style={{ color: "#92400E" }}>Lord</th>
                  </tr>
                </thead>
                <tbody>
                  {planetNak.map((p) => (
                    <tr
                      key={p.planet}
                      className="border-t"
                      style={{ borderColor: "#FDE68A" }}
                      data-testid={`nakshatra-row-${p.planet.toLowerCase()}`}
                    >
                      <td className="px-2 py-1 font-semibold">
                        {p.planet}
                        {p.retrograde && (
                          <span className="ml-1 text-[9px] font-bold" style={{ color: "#B91C1C" }}>R</span>
                        )}
                      </td>
                      <td className="px-2 py-1">{p.rashi || "—"}</td>
                      <td className="px-2 py-1">{p.nakshatra || "—"}</td>
                      <td className="px-2 py-1 text-center">{p.pada ?? "—"}</td>
                      <td className="px-2 py-1" style={{ color: "#6B7280" }}>{p.nakshatra_lord || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Dasha List Modal (Vimshottari MD → AD → PD drill-down) ──── */
function DashaListModal({ mahadashas, currentLord, customerName, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  // Track which Mahadasha and Antardasha are expanded. Only one MD open at
  // a time keeps the modal scannable; the AD set inside the open MD can
  // hold one open at a time too.
  const [openMd, setOpenMd] = useState(null);
  const [openAd, setOpenAd] = useState(null);

  const isWithin = (from, to) => today >= from && today < to;
  const isPast = (to) => to <= today;

  return (
    <div
      className="fixed inset-0 z-[125] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dasha-list-modal"
    >
      <div
        className="relative rounded-2xl p-5 sm:p-6 max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        style={{ backgroundColor: "#FFFBF5", color: "#1A1410" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-1.5 hover:bg-amber-100"
          aria-label="Close"
          data-testid="close-dasha-modal-btn"
        >
          <X size={18} weight="bold" style={{ color: "#92400E" }} />
        </button>
        <p className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: "#92400E" }}>
          <MoonStars size={12} weight="fill" />
          Vimshottari Mahadasha
        </p>
        <h3 className="font-serif-display text-xl mt-0.5" style={{ color: "#1A1410" }}>
          {customerName || "Seeker"} &middot; Dasha timeline
        </h3>
        <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
          Tap a Mahadasha to drill into Antardasha → Pratyantardasha.
        </p>

        {mahadashas.length === 0 ? (
          <p className="mt-6 text-sm" style={{ color: "#6B7280" }}>
            Dasha timeline unavailable. Make sure the seeker&apos;s birth details are complete.
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {mahadashas.map((md, idx) => {
              const mdCurrent = isWithin(md.from, md.to);
              const mdPast = isPast(md.to);
              const isOpen = openMd === idx;
              return (
                <li
                  key={`${md.lord}-${idx}`}
                  className="rounded-md"
                  style={{
                    backgroundColor: mdCurrent ? "#FEF3C7" : "#FFFFFF",
                    borderLeft: mdCurrent ? "4px solid #D97706" : "4px solid #FCD7A0",
                    opacity: mdPast && !isOpen ? 0.55 : 1,
                  }}
                  data-testid={`dasha-row-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenMd(isOpen ? null : idx);
                      setOpenAd(null);
                    }}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-50/60 rounded-md transition"
                    data-testid={`dasha-md-toggle-${idx}`}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[10px] font-mono font-bold w-5 text-center" style={{ color: "#92400E" }}>
                        {idx + 1}
                      </span>
                      <div className="min-w-0 text-left">
                        <p className="font-semibold text-sm leading-tight" style={{ color: "#1A1410" }}>
                          {md.lord}
                          {(mdCurrent || md.lord === currentLord) && (
                            <span
                              className="ml-2 align-middle text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: "#D97706", color: "#FFFFFF" }}
                            >
                              Now
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] font-mono leading-tight" style={{ color: "#6B7280" }}>
                          {md.from} → {md.to}
                        </p>
                      </div>
                    </div>
                    <span className="flex items-center gap-2">
                      <span className="text-[11px] font-mono" style={{ color: "#1A1410" }}>
                        {Math.round(md.years)}y
                      </span>
                      <span
                        className="text-[12px] transition-transform"
                        style={{
                          color: "#92400E",
                          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                        }}
                      >
                        ▸
                      </span>
                    </span>
                  </button>

                  {isOpen && Array.isArray(md.antardashas) && (
                    <ul
                      className="ml-6 mt-1 mb-2 space-y-1 border-l-2"
                      style={{ borderColor: "#FCD7A0" }}
                      data-testid={`dasha-ad-list-${idx}`}
                    >
                      {md.antardashas.map((ad, adIdx) => {
                        const adCurrent = isWithin(ad.from, ad.to);
                        const adOpen = openAd === adIdx;
                        return (
                          <li key={`${ad.lord}-${adIdx}`} className="ml-2">
                            <button
                              type="button"
                              onClick={() => setOpenAd(adOpen ? null : adIdx)}
                              className="w-full px-2.5 py-1.5 flex items-center justify-between rounded hover:bg-amber-50 transition"
                              style={{
                                backgroundColor: adCurrent ? "#FEF3C7" : "transparent",
                              }}
                              data-testid={`dasha-ad-toggle-${idx}-${adIdx}`}
                              aria-expanded={adOpen}
                            >
                              <div className="flex items-center gap-2 min-w-0 text-left">
                                <span className="text-[10px] font-mono" style={{ color: "#92400E" }}>
                                  {adIdx + 1}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold leading-tight" style={{ color: "#1A1410" }}>
                                    {md.lord} &rarr; {ad.lord}
                                    {adCurrent && (
                                      <span
                                        className="ml-1.5 text-[8px] uppercase tracking-widest font-bold px-1 py-0.5 rounded"
                                        style={{ backgroundColor: "#D97706", color: "#FFFFFF" }}
                                      >
                                        Now
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-[10px] font-mono leading-tight" style={{ color: "#6B7280" }}>
                                    {ad.from} → {ad.to}
                                  </p>
                                </div>
                              </div>
                              <span
                                className="text-[10px] transition-transform"
                                style={{
                                  color: "#92400E",
                                  transform: adOpen ? "rotate(90deg)" : "rotate(0deg)",
                                }}
                              >
                                ▸
                              </span>
                            </button>

                            {adOpen && Array.isArray(ad.pratyantardashas) && (
                              <ul
                                className="ml-5 mt-0.5 mb-1 space-y-0.5 border-l"
                                style={{ borderColor: "#FCD7A0" }}
                                data-testid={`dasha-pd-list-${idx}-${adIdx}`}
                              >
                                {ad.pratyantardashas.map((pd, pdIdx) => {
                                  const pdCurrent = isWithin(pd.from, pd.to);
                                  return (
                                    <li
                                      key={`${pd.lord}-${pdIdx}`}
                                      className="ml-2 px-2 py-1 flex items-center justify-between rounded"
                                      style={{
                                        backgroundColor: pdCurrent ? "#FEF3C7" : "transparent",
                                      }}
                                      data-testid={`dasha-pd-row-${idx}-${adIdx}-${pdIdx}`}
                                    >
                                      <span className="text-[11px]" style={{ color: "#1A1410" }}>
                                        {md.lord} &rarr; {ad.lord} &rarr;{" "}
                                        <span className="font-semibold">{pd.lord}</span>
                                        {pdCurrent && (
                                          <span
                                            className="ml-1.5 text-[7px] uppercase tracking-widest font-bold px-1 py-0.5 rounded"
                                            style={{ backgroundColor: "#D97706", color: "#FFFFFF" }}
                                          >
                                            Now
                                          </span>
                                        )}
                                      </span>
                                      <span className="text-[9px] font-mono" style={{ color: "#6B7280" }}>
                                        {pd.from} → {pd.to}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


/* ─── Incoming Call Banner ───────────────────────────────────────
   Heads-up shown the moment a seeker initiates a voice call.
   PSTN: Twilio is ringing the astrologer's phone in parallel — this
   banner just makes sure they don't miss it AND surfaces the seeker's
   birth chart so they walk into the call already prepped.
   WebRTC: same banner; the in-portal Voice SDK ring overlay handles
   the actual accept/decline. */
function IncomingCallBanner({ call, onDismiss }) {
  const bd = call?.seeker_birth || null;
  const sum = call?.seeker_summary || {};
  const isRinging = call?.status === "ringing";
  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[140] w-[calc(100vw-1.5rem)] max-w-md rounded-2xl shadow-2xl border-2 animate-[pulse_2s_ease-in-out_infinite]"
      style={{
        backgroundColor: "#FFFBF5",
        borderColor: isRinging ? "#16A34A" : "#D97706",
      }}
      data-testid="incoming-call-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className="rounded-full p-2.5 shrink-0"
          style={{
            backgroundColor: isRinging ? "#DCFCE7" : "#FEF3C7",
            color: isRinging ? "#15803D" : "#92400E",
          }}
        >
          <Phone size={20} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5"
            style={{ color: isRinging ? "#15803D" : "#92400E" }}
          >
            {isRinging ? "Call ringing — pick up your phone" : "Call in progress"}
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: isRinging ? "#16A34A" : "#D97706" }}
            />
          </p>
          <h3 className="font-serif-display text-lg mt-0.5" style={{ color: "#1A1410" }} data-testid="incoming-call-name">
            {call?.seeker_name || "Anonymous seeker"}
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: "#6B7280" }}>
            {call?.mode === "pstn" ? "Phone call · Twilio bridge" : "In-app call · WebRTC"}
          </p>

          {bd ? (
            <div className="mt-2 text-[11px] leading-relaxed" style={{ color: "#1A1410" }} data-testid="incoming-call-birth">
              <div className="flex items-center gap-1.5">
                <CalendarBlank size={11} weight="fill" /> {bd.date} · {bd.time}
                {bd.timezone ? <span style={{ color: "#6B7280" }}> ({bd.timezone})</span> : null}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5 truncate">
                <MapPin size={11} weight="fill" /> <span className="truncate">{bd.place}</span>
              </div>
              {(sum.lagna || sum.moon_rashi) && (
                <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5">
                  {sum.lagna && (
                    <div>
                      <span style={{ color: "#6B7280" }}>Lagna:</span>{" "}
                      <span className="font-semibold">{sum.lagna}</span>
                    </div>
                  )}
                  {sum.moon_rashi && (
                    <div>
                      <span style={{ color: "#6B7280" }}>Moon:</span>{" "}
                      <span className="font-semibold">{sum.moon_rashi}</span>
                      {sum.nakshatra ? <span> · {sum.nakshatra}</span> : null}
                    </div>
                  )}
                  {sum.current_md && (
                    <div className="col-span-2">
                      <span style={{ color: "#6B7280" }}>Dasha:</span>{" "}
                      <span className="font-semibold">
                        {sum.current_md}
                        {sum.current_ad ? <> &rarr; {sum.current_ad}</> : null}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-[11px] mt-1.5" style={{ color: "#92400E" }}>
              No birth details on file for this seeker.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full p-1.5 hover:bg-amber-100 shrink-0"
          aria-label="Dismiss"
          data-testid="incoming-call-dismiss"
        >
          <X size={16} weight="bold" style={{ color: "#92400E" }} />
        </button>
      </div>
    </div>
  );
}


/* ─── Push-notification opt-in banner ───────────────────────────────────
   Astrologers run this app from their home-screen / PWA. We want
   incoming chat & call rings to surface as a system notification on
   their phone even when the tab is backgrounded. This banner:
     • Hidden if push isn't supported (e.g. iOS Safari NOT installed to
       Home Screen — push only works as a PWA there).
     • Hidden once subscribed (silent success).
     • One-tap enable, asks permission inside the click handler. */
function PushNotifBanner() {
  const push = usePushNotifications({ mode: "astrologer" });
  // Hide entirely when not supported or already subscribed — banner
  // should be conditional + non-noisy.
  if (!push.supported) {
    // On iOS in a regular tab, give a specific hint instead of nothing.
    if (push.isIos && !push.isStandalone) {
      return (
        <div
          className="mt-4 rounded-xl p-3.5 border-2 border-amber-300 bg-amber-50 text-amber-900 flex items-start gap-3"
          data-testid="push-ios-hint"
        >
          <Bell size={18} weight="fill" className="shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed flex-1">
            <span className="font-bold">Get rings as phone notifications:</span>{" "}
            Open this site in Safari → Share → <em>Add to Home Screen</em>, then
            open it from the home-screen icon. iOS only allows push from
            installed PWAs.
          </div>
        </div>
      );
    }
    return null;
  }
  if (push.subscribed) return null;

  const handleEnable = async () => {
    const res = await push.enable();
    if (res.ok) {
      toast.success("Push notifications enabled — you'll get rings even when this tab is closed.");
    } else if (res.error === "permission_denied") {
      toast.error("Permission denied. Enable notifications for this site in your browser settings.");
    } else {
      toast.error("Couldn't enable push. Try again, or check browser settings.");
    }
  };

  return (
    <div
      className="mt-4 rounded-xl p-3.5 border-2 border-emerald-300 bg-emerald-50 text-emerald-900 flex items-center gap-3"
      data-testid="push-enable-banner"
    >
      <Bell size={20} weight="fill" className="shrink-0" />
      <div className="flex-1 text-xs sm:text-sm leading-snug">
        <span className="font-bold">Never miss a seeker.</span>{" "}
        Get an instant phone notification the moment someone rings you for chat or a call.
      </div>
      <button
        type="button"
        onClick={handleEnable}
        disabled={push.busy}
        className="rounded-full px-4 py-2 text-xs sm:text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white shadow-sm transition shrink-0"
        data-testid="push-enable-btn"
      >
        {push.busy ? "Enabling…" : "Enable"}
      </button>
    </div>
  );
}
